import { Button, Table } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { PermissionsRequired } from '@/containers/permission-containers'
import type {
  DriverActivityDayReportQuery,
  DriverActivityDayReportQueryVariables,
} from '@/modules/activities'
import {
  DateWrapper,
  DRIVER_ACTIVITY_DAY_REPORT_QUERY,
  Navigation,
  NextButton,
  optimizeActivities,
  parseNumberToTimestamp,
  PrevButton,
  Schedule,
} from '@/modules/activities'
import type { ExportForm } from '@/modules/exports'
import { Attachments } from '@/modules/issues'
import { format } from '@/utils/date-fns'
import { correctLocalDateWithTimezone } from '@/utils/date/localeCorrection'
import type { FileAttachment } from '@/utils/hooks'
import {
  useBreadcrumbs,
  useOnErrorAuto,
  useParam,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useMutation, useQuery } from '@apollo/client'
import { Icon, Loader, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addDays, formatISO, parseISO, startOfDay, subDays } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import type { ArchivedInfringementForm } from '..'
import {
  INFRINGEMENT_QUERY,
  PATCH_INFRINGEMENT_MUTATION,
  useInfringementsMutations,
} from '..'
import { MainInfo } from '../components'
import { Details } from '../components/Details'
import { ExportInfringementsModal } from '../components/ExportInfringementsModal'
import { ReceiptInfringementModal } from '../components/ReceiptInfringementModal'
import { ArchiveInfringementPrompt } from '../components/table/ArchiveInfringementModal'
import type { InfringementQueryInfringement } from '../types.graphl'
import { H1, H2 } from '@/components'
import { getNodes } from '@/utils/graphql'
import type { Issue } from '@/modules/activities/components/detail/Schedule.types'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};

  /* TODO this is invalid, should it be enabled? */
  s header {
    display: flex;
    justify-content: space-between;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: ${isMobile ? 'column' : 'row'};
  gap: 1rem;

  margin: 1rem 0;
  padding: 0 1rem;

  button {
    height: 3rem;
  }
`

const Area = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;

  header {
    display: flex;

    gap: 1rem;
    align-items: center;
    margin: 1rem 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const ExportArchiveButtons = styled.div`
  width: 100%;
  display: flex;
  justify-content: ${isMobile ? 'space-between' : 'flex-end'};
  flex-direction: ${isMobile ? 'column' : 'row'};
  gap: 1rem;
`
const Content = styled(Card)`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow-x: auto;
`

const NoOpenIssueWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.text.dark};
`

const NoOpenIssueText = styled.span`
  color: ${props => props.theme.colors.text.dark};
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: default;
`

export const Infringement: React.FC = () => {
  const translations = useTranslate({
    infringement: 'common.infringement',
    loading: 'common.loading...',
    activities: 'common.activities',
    seeInActivities: 'infringements.see-in-activities',

    actionLogTitle: 'issues.action-log',

    open: 'common.open-alt',
    close: 'common.close',
    reopen: 'common.reopen',
    closed: 'common.closed',
    archived: ['common.archived', val => val.toLowerCase()],

    date: 'common.date',
    about: 'common.about',

    export: 'common.export',
    archive: 'common.archive',
    restore: 'common.restore',
    relatedIssues: 'infringements.related-issues',

    receipt: 'infringements.receipt',
    action: 'common.action',
    timestamp: 'common.timestamp',

    actionLog: {
      CREATED: ['issues.created-by-user', { user: 'user' }],
      UPDATED: ['issues.updated-by-user', { user: 'user' }],

      ARCHIVED: ['issues.archived-by-user', { user: 'user' }],
      RESTORED: ['issues.restored-by-user', { user: 'user' }],

      CLOSED: ['issues.closed-by-user', { user: 'user' }],
      REOPENED: ['issues.reopened-by-user', { user: 'user' }],

      REMINDER: [
        'issues.reminder-by-user-to-user',
        { user: 'user', target: 'target' },
      ],
    },
  })

  const navigate = useNavigate()

  const infringementId = useParam('infringementId')

  const { exportInfringement, loading } = useInfringementsMutations()
  const { setOverride } = useBreadcrumbs()
  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()
  const me = useUser()
  const { hasPermissions } = usePermissions()
  const canClose = hasPermissions(
    PERMISSIONS.infringements.view.othersinfringement,
    true
  )
  const [activitiesDate, setActivitiesDate] = useState<Date | null>(null)

  const { data, loading: queryLoading } = useQuery(INFRINGEMENT_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      id: infringementId,
    },
    onCompleted(data) {
      if (!data.infringement) void navigate('/infringements')

      const { infringement } = data
      const date = format(new Date(infringement.createdAt), 'PP')
      setOverride(infringement.id, date)
      setActivitiesDate(parseISO(infringement.infringementTimestamp))
    },
    onError: onErrorAuto(),
  })

  const infringement = data?.infringement
  const attachments = getNodes(infringement?.attachments)
  const infringementTimestamp = infringement
    ? parseISO(infringement?.infringementTimestamp)
    : undefined
  const isClosed = infringement?.closedAt !== null
  const userId = infringement?.user.id ?? me.id

  const timestamp = activitiesDate ? activitiesDate : new Date()
  const tempDate = startOfDay(timestamp)

  const { data: activityData, refetch } = useQuery<
    DriverActivityDayReportQuery,
    DriverActivityDayReportQueryVariables
  >(DRIVER_ACTIVITY_DAY_REPORT_QUERY, {
    fetchPolicy: 'network-only',
    skip: !userId || !infringement,
    variables: {
      user: userId,
      date: formatISO(tempDate),
    },
    onError: onErrorAuto(),
    onCompleted: data => {
      if (
        data.driverActivityDayReport.timezoneData?.offset &&
        infringement?.infringementTimestamp
      ) {
        // This is pretty ugly, but untill we have a CompanyTimezone solution this'll refetch
        // the correct activities if the user's browser is in a different timezone than
        // the report's timezone. Additionally this is required to correct the date of
        // of the report if the user's browser is in a timezone that's -x UTC and report's +y UTC while x > y on
        // the day to not get the previous day.
        //TODO: clean this up when CompanyTimezone is implemented
        const offset = data.driverActivityDayReport.timezoneData?.offset
        if (offset !== tempDate.getTimezoneOffset() / -60) {
          const newFetchDateString =
            infringement?.infringementTimestamp +
            `T00:00:00+` +
            parseNumberToTimestamp(offset)

          void refetch({
            user: userId,
            date: newFetchDateString,
          })
        }
      }
    },
  })
  const [patchInfringement] = useMutation(PATCH_INFRINGEMENT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['Infringement'],
    onError: onErrorAuto(),
  })

  const [receiptInfringement] = useMutation(PATCH_INFRINGEMENT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['Infringement', 'AllInfringements', 'AllIssues'],
    onError: onErrorAuto(),
  })

  const isLoading = queryLoading || loading || !data

  const articles = infringement?.infringementCategory?.legalBases?.edges.map(
    edge => edge?.node?.article ?? ''
  )

  const startDate = infringement?.periodStartDate
    ? parseISO(infringement?.periodStartDate)
    : new Date()
  const endDate = infringement?.periodEndDate
    ? parseISO(infringement?.periodEndDate)
    : new Date()

  const report = activityData?.driverActivityDayReport ?? null
  const issues = [
    {
      id: infringement?.id,
      timeOfIssueStart: infringement?.infringementTimestamp ?? '',
      timeOfIssueEnd: infringement?.infringementTimestamp ?? '',
      isInfringement: true,
    } as Issue,
  ]
  const specificConditions = activityData?.specificConditions ?? []

  const date = startOfDay(
    correctLocalDateWithTimezone(timestamp, report?.timezoneData)
  )

  async function handleArchiveInfringement(
    infringement?: InfringementQueryInfringement
  ) {
    if (typeof infringement === 'undefined') return

    if (infringement.archivedAt !== null) {
      void executeArchiveInfringement(infringement)()
      return
    }

    const initialData: ArchivedInfringementForm = {
      archivedBasis: '',
    }

    const { data } = await addPrompt<ArchivedInfringementForm | null>(
      resolve => (
        <ArchiveInfringementPrompt
          initialData={initialData}
          onResolve={resolve}
        />
      )
    )
    if (data === null) return

    void executeArchiveInfringement(infringement)(data)
  }

  function executeArchiveInfringement(
    infringement?: InfringementQueryInfringement | null
  ) {
    if (!infringement) return () => void 0

    return async (data?: ArchivedInfringementForm) => {
      const isArchived = infringement.archivedAt !== null
      await patchInfringement({
        variables: {
          id: infringement.id,
          input: {
            archivedBy: me.id,
            archivedAt: isArchived ? null : formatISO(new Date()),
            archivedBasis: data?.archivedBasis,
          },
        },
      })
    }
  }

  async function handleExport() {
    if (!infringement) return

    const { data } = await addPrompt<ExportForm | null>(resolve => (
      <ExportInfringementsModal onSubmit={resolve} />
    ))
    if (!data) return

    const recipients = data.recipients.length === 0 ? [me.id] : data.recipients

    void exportInfringement({
      variables: {
        infringementId: infringementId,
        exportOption: data.exportOption,
        recipientId: me.id,
        recipientEmails: recipients,
      },
    })
  }

  async function handleReceiptInfringement() {
    const { data } = await addPrompt<{
      reason: string
      attachmentsAdd: Omit<FileAttachment, 'id'>[]
      attachmentsRemove: string[]
    } | null>(resolve => (
      <ReceiptInfringementModal
        initialData={{
          reason: infringement?.issueReason,
          attachments: attachments,
        }}
        onSubmit={resolve}
      />
    ))
    if (!data || !data.reason || !infringement) return

    await receiptInfringement({
      variables: {
        id: infringement.id,
        input: {
          solution: data.reason,
          attachmentsAdd: data.attachmentsAdd,
          attachmentsRemove: data.attachmentsRemove,
        },
      },
    })
  }

  async function handleCloseInfringement() {
    if (!infringement) return
    await patchInfringement({
      variables: {
        id: infringement.id,
        input: {
          closedAt: infringement.closedAt ? null : formatISO(new Date()),
          closedBy: me.id,
        },
      },
    })
  }

  useEffect(() => {
    if (data) return
    setOverride(infringementId, translations.loading)
  }, [data, infringementId, setOverride, translations.loading])

  // Activities processing
  const activities = report?.activities ?? []
  // const vehicleColors = useMemo<VehicleColorDictionary>(() => {
  //   const vehicleObject: VehicleColorDictionary = {}
  //   const vehicleList =
  //     report?.drivenCoordinates
  //       .map(drivenCoordinate => drivenCoordinate.vehicleVin ?? 'Unknown')
  //       .filter(
  //         (value, index, vehicles) => vehicles.indexOf(value) === index
  //       ) ?? []
  //   const vehicleColorList = generateActivityMapColors(vehicleList.length)

  //   vehicleList.forEach((vehicle, idx) => {
  //     vehicleObject[vehicle] = vehicleColorList[idx] ?? '#0088FF'
  //   })

  //   return vehicleObject
  // }, [report?.drivenCoordinates])

  const optimizedActivities = optimizeActivities(activities)

  const actionLogs = getNodes(infringement?.actionLogs)

  return (
    <Wrapper>
      <Area>
        <header>
          <H1>{translations.infringement}</H1>
        </header>

        <Content>
          {isLoading && <Loader.Dots />}
          {infringement && (
            <>
              <MainInfo
                loading={queryLoading}
                category={infringement.infringementCategory}
                archivedAt={infringement?.archivedAt}
                timestamp={infringementTimestamp}
                startDate={startDate}
                endDate={endDate}
              />

              <Details
                loading={queryLoading}
                reasonLowerLimit={infringement.infringementCategory.lowerLimit}
                reason={infringement.infringementCategory?.description}
                reasonUpperLimit={infringement.infringementCategory?.upperLimit}
                userFullname={infringement.user?.fullName}
                reasonValue={infringement.reasonValue}
                archivedAt={infringement.archivedAt}
                archivedBasis={infringement.archivedBasis}
                solution={infringement.solution ?? infringement.issueReason}
                legalBasis={articles}
              />
              <Attachments
                editing={false}
                attachments={attachments}
                onUpdate={() => void 0}
              />
            </>
          )}
        </Content>

        {infringement && (
          <ButtonWrapper>
            <PermissionsRequired
              permissions={PERMISSIONS.companies.export.exportReports}
            >
              <Button
                disabled={isLoading || !infringement}
                padding="0 2rem"
                onClick={() => handleExport()}
              >
                <Icon icon="inbox-in" fixedWidth margin="0 6px 0 0" />
                {translations.export}
              </Button>
            </PermissionsRequired>

            <PermissionsRequired
              permissions={PERMISSIONS.infringements.change.infringement}
            >
              <Button
                disabled={isLoading || !infringement}
                padding="0 2rem"
                onClick={() => handleArchiveInfringement(infringement)}
              >
                {infringement?.archivedAt === null && (
                  <Icon icon="archive" fixedWidth margin="0 6px 0 0" />
                )}
                {infringement?.archivedAt !== null
                  ? translations.restore
                  : translations.archive}
              </Button>
            </PermissionsRequired>
            <ExportArchiveButtons>
              {!isClosed ? (
                <>
                  <Button
                    disabled={isLoading || !infringement}
                    padding="0 2rem"
                    onClick={() => handleReceiptInfringement()}
                  >
                    <Icon icon="receipt" fixedWidth margin="0 6px 0 0" />
                    {translations.receipt}
                  </Button>
                  {canClose && (
                    <Button
                      disabled={isLoading || !infringement}
                      padding="0 2rem"
                      onClick={() => handleCloseInfringement()}
                    >
                      <Icon icon="check" fixedWidth margin="0 6px 0 0" />
                      {translations.close}
                    </Button>
                  )}
                </>
              ) : (
                <NoOpenIssueWrapper>
                  <Icon
                    icon="check"
                    color="green"
                    fixedWidth
                    margin="0 6px 0 0"
                  />
                  <NoOpenIssueText>{translations.closed}</NoOpenIssueText>
                  {canClose && (
                    <Button
                      disabled={isLoading || !infringement}
                      padding="0 2rem"
                      onClick={() => handleCloseInfringement()}
                    >
                      <Icon icon="check" fixedWidth margin="0 6px 0 0" />
                      {translations.reopen}
                    </Button>
                  )}
                </NoOpenIssueWrapper>
              )}
            </ExportArchiveButtons>
          </ButtonWrapper>
        )}
      </Area>

      {infringementId && !queryLoading && infringement && (
        <>
          <Area>
            <header>
              <H2>{translations.activities}</H2>
              <Navigation>
                <PrevButton onClick={() => setActivitiesDate(subDays(date, 1))}>
                  <Icon icon="chevron-left" />
                </PrevButton>
                <DateWrapper>{format(date, 'PP')}</DateWrapper>
                <NextButton onClick={() => setActivitiesDate(addDays(date, 1))}>
                  <Icon icon="chevron-right" />
                </NextButton>
              </Navigation>
            </header>
            <Card $padding="0">
              {report && (
                <Schedule
                  times={report}
                  activities={optimizedActivities}
                  issues={issues}
                  infringements={[infringement]}
                  specificConditions={specificConditions}
                  report={report}
                  date={date}
                  infoTooltip={report?.registrationNumbers ?? []}
                  variant="day"
                />
              )}
            </Card>
          </Area>
        </>
      )}
      {actionLogs.length > 0 && (
        <>
          <H2 className="mt-8">{translations.actionLogTitle}</H2>
          <Table>
            <thead>
              <tr>
                <th>{translations.timestamp}</th>
                <th>{translations.action}</th>
              </tr>
            </thead>

            <tbody>
              {actionLogs.map(node => {
                const createdAt = format(new Date(node.createdAt), 'PPpp')
                const user = node.user ? node.user.fullName : 'System'
                const secondaryUser = node.secondaryUser
                  ? node.secondaryUser.fullName
                  : ''
                const actionString = node.secondaryUser
                  ? translations.actionLog.REMINDER({
                      user: user,
                      target: secondaryUser,
                    })
                  : translations.actionLog[node.action]({ user: user })

                return (
                  <tr key={node.id}>
                    <td>{createdAt}</td>
                    <td>{actionString}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </>
      )}
    </Wrapper>
  )
}
